import { useState, useEffect, useCallback, useContext, createContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import log from 'loglevel';
import moment from "moment";
import 'moment/locale/pt';

import loginApi from "./backend/loginApi";

import LocaleConfig  from "../config/LocaleConfig";
import { HttpError } from "../helpers/custom-errors";
import { isArrayWithLength } from "../helpers/commons";

const configContext = createContext();

//Provider component that wraps your app and makes configuration object ...
//... available to any child component that calls useConfiguration().
export function ProvideConfig({ children }) {
	const config = useProvideConfig();
	return <configContext.Provider value={config}>{children}</configContext.Provider>;
}

//Hook for child components to get the configuration object ...
//... and re-render when it changes.
export const useConfig = () => {
	return useContext(configContext);
};

//Provider hook that creates configuration object and handles state
const useProvideConfig = () => {
	const [initialized, setInitialized] = useState(false);
	const [globalError, setGlobalError] = useState(null);
	const [loading, setLoading] = useState(false);
	/*const [config, setConfig] = useState(null);*/

	const [availableLanguages, setAvailableLanguages] = useState([]);
	const [preferredLanguage, setPreferredLanguage] = useState(null);
	/* Ambiente do SERVIDOR (`dev` | `prod`) — vem no mesmo payload de boot, sem
	   fetch extra. Serve pra tela avisar em qual ambiente o operador está: lab e
	   produção abertos lado a lado se confundem. */
	const [environment, setEnvironment] = useState(null);
	const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null);
	const [numberFormat, setNumberFormat] = useState(null);
	const [uploadFileSizeLimit, setUploadFileSizeLimit] = useState(null);

	const { t, i18n } = useTranslation();

	// Tracker de mount pra evitar setState em componente desmontado durante retry.
	// Ref (não state) porque não queremos disparar re-render — só saber se ainda
	// vale aplicar o resultado.
	const isMountedRef = useRef(true);

	const loadConfig = useCallback(() => {
		setLoading(true);
		setGlobalError(null);
		return loginApi.getConfig()
			.then((response) => {
				if (isMountedRef.current) {
					setRecaptchaSiteKey(response.recaptcha.siteKey);
					setEnvironment(response.environment || null);
					setAvailableLanguages(response.language.available);
					setPreferredLanguage(response.language.preferred);
					setNumberFormat(response.numberformat);
					setUploadFileSizeLimit(response.uploadFileSizeLimit);

					//Update with preferred Language/Locale
					i18n.changeLanguage(response.language.preferred);

					//Define Moment Locales
					if (isArrayWithLength(response.language.available)) {
						response.language.available.forEach(item => {
							let config = LocaleConfig.MomentJS[item];
							if (config) {
								moment.locale(item, config);
							}
						});
					}
					moment.locale(response.language.preferred);
				}

				if (response.loglevel) log.setLevel(response.loglevel);
			})
			.catch(error => {
				log.error(error);
				if (isMountedRef.current) {
					setGlobalError(new HttpError('GENERIC', 500, t('error.api.load-page')));
				}
			})
			.finally(() => {
				if (isMountedRef.current) {
					setInitialized(true);
					setLoading(false);
				}
			});
	}, [i18n, t]);

	useEffect(() => {
		isMountedRef.current = true;
		loadConfig();
		return () => { isMountedRef.current = false; };
		// loadConfig é estável (useCallback com deps constantes por sessão).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
     
     const handleLanguageChange = (lng, cb) => {  
		  loginApi.updateLanguage({ language: lng })
			.then((response) => {
				
				log.info("Language Updated Successfully: ", lng);
				
				i18n.changeLanguage(lng);
				moment.locale(lng);
				
				setPreferredLanguage(lng);

				cb("success", response.success);
			})
			.catch(error => {		
				
				log.error("Language Updated Failed: ", error);
				
				cb("error", error.message);
			})
	 };
    
    
//Return the config parameters
  return {
	  initialized,
	  globalError,
	  loading,
	  retry: loadConfig,
	  availableLanguages,
	  preferredLanguage,
	  handleLanguageChange,
	  environment,
	  recaptchaSiteKey,
	  numberFormat,
	  uploadFileSizeLimit
  };

}